/**
 * Native date formatting utilities using Intl API (no dependencies like moment)
 * Reduces bundle size significantly
 */

/**
 * Format date to DD-MM-YYYY
 */
export const formatDate = (date?: string | Date | null): string => {
  if (!date) return "";
  try {
    const d = typeof date === "string" ? new Date(date) : date;
    if (isNaN(d.getTime())) return "";
    
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    
    return `${day}-${month}-${year}`;
  } catch {
    return "";
  }
};

/**
 * Format date to HH:mm (24-hour format)
 */
export const formatTime = (date?: string | Date | null): string => {
  if (!date) return "";
  try {
    const d = typeof date === "string" ? new Date(date) : date;
    if (isNaN(d.getTime())) return "";
    
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    
    return `${hours}:${minutes}`;
  } catch {
    return "";
  }
};

/**
 * Calculate difference in days between two dates
 */
export const getDaysDifference = (date1: string | Date, date2?: string | Date): number => {
  try {
    const d1 = typeof date1 === "string" ? new Date(date1) : date1;
    const d2 = date2 ? (typeof date2 === "string" ? new Date(date2) : date2) : new Date();
    
    if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return 0;
    
    const diffTime = d1.getTime() - d2.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  } catch {
    return 0;
  }
};

/**
 * Get date string from D-MM-YYYY format
 */
export const parseCustomDateFormat = (dateStr: string): string => {
  try {
    // Parse "D-MM-YYYY" to Date, then format to "ddd, D MMM"
    const parts = dateStr.split("-");
    if (parts.length !== 3) return dateStr;
    
    const [day, month, year] = parts;
    const date = new Date(`${year}-${month}-${day}`);
    
    if (isNaN(date.getTime())) return dateStr;
    
    const options: Intl.DateTimeFormatOptions = {
      weekday: "short",
      day: "numeric",
      month: "short",
    };
    
    return new Intl.DateTimeFormat("en-US", options).format(date);
  } catch {
    return dateStr;
  }
};

/**
 * Get today's date in D-MM-YYYY format
 */
export const getTodayDateString = (): string => {
  const now = new Date();
  const day = now.getDate();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const year = now.getFullYear();
  
  return `${day}-${month}-${year}`;
};

/**
 * Get yesterday's date in D-MM-YYYY format
 */
export const getYesterdayDateString = (): string => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  
  const day = yesterday.getDate();
  const month = String(yesterday.getMonth() + 1).padStart(2, "0");
  const year = yesterday.getFullYear();
  
  return `${day}-${month}-${year}`;
};

/**
 * Convert custom date format (D-MM-YYYY) to timestamp for sorting
 */
export const getTimestampFromDateString = (dateStr: string): number => {
  try {
    const parts = dateStr.split("-");
    if (parts.length !== 3) return 0;
    
    const [day, month, year] = parts;
    const date = new Date(`${year}-${month}-${day}`);
    
    return isNaN(date.getTime()) ? 0 : date.getTime();
  } catch {
    return 0;
  }
};
